/**
 * Composable to manage subtitles on an <audio> element.
 *
 * Unlike <video>, an <audio> element has no visual subtitle rendering, so we
 * display cue text in a custom overlay. We deliberately DO NOT rely on the
 * browser's native TextTrack/cue machinery here: Safari (iOS in particular)
 * does not reliably load or fire `cuechange` for <track> elements attached to
 * an <audio> element, so the cue text never appears on mobile.
 *
 * Instead we fetch the active track's WebVTT file ourselves, parse it once,
 * and resolve the active cue from the audio element's `currentTime`. This is
 * fully cross-browser and works the same on desktop and mobile.
 */

import { ref, watch, onUnmounted, type Ref } from 'vue'
import type { SubtitleTrack } from '../../types/asset'

interface ParsedCue {
  start: number
  end: number
  text: string
}

/** Parse a WebVTT timestamp ("HH:MM:SS.mmm" or "MM:SS.mmm") into seconds. */
function parseTimestamp(value: string): number {
  const parts = value.trim().split(':').map((p) => Number(p) || 0)
  let total = 0
  for (const part of parts) {
    total = total * 60 + part
  }
  return total
}

/** Minimal WebVTT parser: extracts timed cues, stripping inline tags. */
function parseVtt(content: string): ParsedCue[] {
  const cues: ParsedCue[] = []
  const blocks = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n\n')

  for (const block of blocks) {
    const lines = block.split('\n').filter((line) => line.trim() !== '')
    if (lines.length === 0) continue

    // The timing line contains "-->"; an optional cue identifier precedes it.
    const timingIndex = lines.findIndex((line) => line.includes('-->'))
    if (timingIndex === -1) continue

    const timingLine = lines[timingIndex] ?? ''
    const match = timingLine.match(/([0-9:.]+)\s*-->\s*([0-9:.]+)/)
    if (!match || !match[1] || !match[2]) continue

    const start = parseTimestamp(match[1])
    const end = parseTimestamp(match[2])
    const text = lines
      .slice(timingIndex + 1)
      .join('\n')
      .replace(/<[^>]+>/g, '') // strip VTT inline tags (rendered as plain text)
      .trim()

    if (text) cues.push({ start, end, text })
  }

  return cues
}

export function useAudioSubtitles(
  audioRef: Ref<HTMLAudioElement | null>,
  tracks: Ref<SubtitleTrack[] | undefined>,
  enabled: Ref<boolean>,
  activeTrackIndex: Ref<number>
) {
  const activeCueText = ref('')

  let cues: ParsedCue[] = []
  let loadedSrc: string | null = null
  let loadToken = 0
  let boundTimeUpdate: (() => void) | null = null

  function updateActiveCue(): void {
    const el = audioRef.value
    if (!el || !enabled.value) {
      activeCueText.value = ''
      return
    }
    const time = el.currentTime
    const cue = cues.find((c) => time >= c.start && time < c.end)
    activeCueText.value = cue ? cue.text : ''
  }

  async function loadCues(): Promise<void> {
    const track = tracks.value?.[activeTrackIndex.value]

    if (!track || !enabled.value) {
      cues = []
      loadedSrc = null
      activeCueText.value = ''
      return
    }

    // Already have this track's cues parsed — just refresh the displayed cue.
    if (loadedSrc === track.src) {
      updateActiveCue()
      return
    }

    const token = ++loadToken
    try {
      const res = await fetch(track.src)
      if (!res.ok) return
      const content = await res.text()
      // A newer load was requested while we awaited — discard stale result.
      if (token !== loadToken) return
      cues = parseVtt(content)
      loadedSrc = track.src
      updateActiveCue()
    } catch {
      // Network/parse error — leave the overlay empty rather than crash.
    }
  }

  function attachListeners(el: HTMLAudioElement): void {
    detachListeners()
    boundTimeUpdate = updateActiveCue
    el.addEventListener('timeupdate', boundTimeUpdate)
    el.addEventListener('seeking', boundTimeUpdate)
    el.addEventListener('seeked', boundTimeUpdate)
  }

  function detachListeners(): void {
    const el = audioRef.value
    if (el && boundTimeUpdate) {
      el.removeEventListener('timeupdate', boundTimeUpdate)
      el.removeEventListener('seeking', boundTimeUpdate)
      el.removeEventListener('seeked', boundTimeUpdate)
    }
    boundTimeUpdate = null
  }

  // Re-resolve cues whenever the source data, selected track, or enabled state
  // changes.
  watch([tracks, activeTrackIndex, enabled], () => {
    loadCues()
  }, { immediate: true })

  // Bind cue polling to the audio element once it mounts.
  watch(audioRef, (el) => {
    detachListeners()
    if (el) {
      attachListeners(el)
      loadCues()
    }
  }, { immediate: true })

  onUnmounted(() => {
    detachListeners()
  })

  function setActiveTrack(index: number): void {
    activeTrackIndex.value = index
  }

  function setEnabled(value: boolean): void {
    enabled.value = value
  }

  return {
    activeCueText,
    setActiveTrack,
    setEnabled,
  }
}
